import { useCallback, useEffect, useRef, useState } from 'react'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'

import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { useLaunchHaven } from '@/features/adviser-chat/hooks/useLaunchHaven'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector, useScrollRef } from '@/utils/sharedHooks'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { COMPLETE_ONBOARDING_KEY } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { Button, Icon, Image, Progress, Text } from '@/ui'
import { TypographyStyles, TextColors, mtc } from '@/styles'
import { useScrollportPadding } from '@/hooks/useScrollportPadding'
import { OnboardingStickyNav } from '../shared/OnboardingStickyNav'
import { useFixedFooterOffset } from '../shared/useFixedFooterOffset'
import {
  completeSeparation,
  fetchSeparationQuestions,
  postSeparationEvent,
  selectSeparationSortedCount,
  submitSeparationAnswers,
  COMPLETE_SEPARATION_KEY,
  SeparationSkipScreen,
  SUBMIT_SEPARATION_ANSWERS_KEY,
  useSeparationQuestions,
  type SeparationAnswerResult,
  type SeparationQuestion,
} from '@/features/onboarding/separation'
import { useCheckmarkBounceAnimation } from './useCheckmarkBounceAnimation'
import { useAutosaveIndicator } from '../shared/useAutosaveIndicator'
import { useAdviserPanelInset } from '../shared/useAdviserPanelInset'
import {
  answersForQuestion,
  hasUnresolvedUnsureNote,
  suggestedAnswer,
  unanimousTopLevelOption,
  type QuestionAnswer,
} from './booksQuestionAnswers'
import { admittedQuestions } from './questionQueue'
import { buildHavenGroupPrompt } from './havenPrompts'
import { BOOKS_QUESTION_ICON_URL } from './booksQuestion.constants'
import { NothingToSort } from './NothingToSort'
import { SeparationLoadError } from '../shared/SeparationLoadError'
import { QuestionCard, type HavenEntryScope } from './QuestionCard'
import { SortingSummary } from './SortingSummary'
import { ONBOARDING_SCREEN } from '../shared/onboardingScreen.styles'
import { plural, transactionNoun } from '../shared/plural'

interface BooksQuestionProps {
  onBack?: () => void
  onContinue?: () => void
  onSkip: (screen: SeparationSkipScreen, openQuestions: number) => void
}

/** The badge's gap from the viewport edge, before Haven's panel is allowed for. */
const AUTOSAVE_INSET = 24

/** How many rows the user settled themselves, as opposed to what pre-sort claimed. */
const clarifiedCountsFrom = (answers: Record<number, QuestionAnswer>) =>
  Object.values(answers).reduce(
    (acc, answer) => {
      Object.values(answer.subItemSelections).forEach((value) => {
        if (value === 'business') acc.business += 1
        else if (value === 'personal') acc.personal += 1
        else if (value === 'unsure') acc.unsure += 1
      })
      return acc
    },
    { business: 0, personal: 0, unsure: 0 }
  )

interface BooksQuestionViewProps extends BooksQuestionProps {
  /** Frozen at mount by the container, so it is a prop rather than a selector read. */
  questions: SeparationQuestion[]
  refreshCounts: () => void
}

const BooksQuestionView = ({
  onBack,
  onContinue,
  onSkip,
  questions,
  refreshCounts,
}: BooksQuestionViewProps) => {
  const sortedCount = useReselector(selectSeparationSortedCount)
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  // Seeded at mount rather than tracking the prop, so a refetch cannot reorder or shorten
  // the list under currentQuestionIndex. It only ever grows, via advanceToNext.
  const [questionsQueue, setQuestionsQueue] = useState<SeparationQuestion[]>(
    () => questions
  )
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [isFinishing, setIsFinishing] = useState(false)
  const [isAllComplete, setIsAllComplete] = useState(false)
  const { checkIconRef } = useCheckmarkBounceAnimation(isAllComplete)
  const { indicatorRef: autosaveIndicatorRef, showSaved } =
    useAutosaveIndicator()
  const { anchorRef: scrollportRef, paddingTop: scrollportPaddingTop } =
    useScrollportPadding()
  const leftOffset = useFixedFooterOffset()
  const panelInset = useAdviserPanelInset()
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const dispatch = useAppDispatch()
  const launchHaven = useLaunchHaven()
  const [firstAnswer] = useState(() => suggestedAnswer(questions[0]))
  const [selectedOption, setSelectedOption] = useState<string | null>(
    firstAnswer.selectedOption
  )
  const [isExpanded, setIsExpanded] = useState(firstAnswer.isExpanded)
  const [subItemSelections, setSubItemSelections] = useState<
    Record<string, string>
  >(firstAnswer.subItemSelections)
  const [note, setNote] = useState(firstAnswer.note)
  const [askedHavenFor, setAskedHavenFor] = useState<Set<number>>(
    () => new Set()
  )
  const [showSelectionError, setShowSelectionError] = useState(false)
  const [showSubItemError, setShowSubItemError] = useState(false)
  const [showNoteError, setShowNoteError] = useState(false)
  const isSaving = useReselector(getIsFetching, SUBMIT_SEPARATION_ANSWERS_KEY)
  const saveFailed = Boolean(
    useReselector(getFetchError, SUBMIT_SEPARATION_ANSWERS_KEY)
  )
  const isRetryingRelease = useReselector(
    getIsFetching,
    COMPLETE_SEPARATION_KEY
  )
  const releaseFailed = Boolean(
    useReselector(getFetchError, COMPLETE_SEPARATION_KEY)
  )
  const isCompleting = useReselector(getIsFetching, COMPLETE_ONBOARDING_KEY)
  const completionFailed = Boolean(
    useReselector(getFetchError, COMPLETE_ONBOARDING_KEY)
  )
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const [answers, setAnswers] = useState<Record<number, QuestionAnswer>>({})
  // Awaited before the summary renders, so it reports totals the writes have landed in.
  const pendingSavesRef = useRef<Promise<SeparationAnswerResult | undefined>[]>(
    []
  )

  const totalSteps = questionsQueue.length
  const question = questionsQueue[currentQuestionIndex]
  // Deliberately not derived from `selectedOption === expandValue`: picking "It's a Mix"
  // expands via `handleSelectOption`, and deriving it too would pin the card open, since the
  // chevron would set `isExpanded` false and the derived clause would immediately win.
  const isExpandedView = isExpanded || Boolean(question.alwaysExpanded)
  const progressValue = isFinishing
    ? 100
    : (currentQuestionIndex / totalSteps) * 100
  // The bar fills with steps completed; the label names the step being answered, so it is
  // one ahead of it.
  const progressLabelValue = isFinishing ? totalSteps : currentQuestionIndex + 1

  useEffect(() => {
    pageView('onboarding books question')
  }, [pageView])

  useEffect(() => {
    postSeparationEvent('shown')
  }, [])

  // Question-to-question and into the completion screen are internal state changes within
  // the same route, so the router's own stage-change scroll never fires for them.
  useEffect(() => {
    scrollToRef()
  }, [currentQuestionIndex, isAllComplete, scrollToRef])

  useEffect(() => {
    if (!isFinishing) return undefined
    const timeoutId = setTimeout(() => {
      setIsAllComplete(true)
      postSeparationEvent('finished')
      // The summary reads server totals, so releasing before the last save lands would
      // show numbers that contradict the answers just given.
      void Promise.all(pendingSavesRef.current)
        .then(() => dispatch(completeSeparation()))
        .then(refreshCounts)
    }, 900)
    return () => clearTimeout(timeoutId)
  }, [dispatch, isFinishing, refreshCounts])

  const goToQuestion = useCallback(
    (targetIndex: number, queue: SeparationQuestion[] = questionsQueue) => {
      setAnswers((prev) => ({
        ...prev,
        [currentQuestionIndex]: {
          selectedOption,
          isExpanded,
          subItemSelections,
          note,
        },
      }))

      const answer = answers[targetIndex] ?? suggestedAnswer(queue[targetIndex])

      setCurrentQuestionIndex(targetIndex)
      setSelectedOption(answer.selectedOption)
      setIsExpanded(answer.isExpanded)
      setSubItemSelections(answer.subItemSelections)
      setNote(answer.note)
      setShowSelectionError(false)
      setShowNoteError(false)
      setShowSubItemError(false)
    },
    [
      currentQuestionIndex,
      selectedOption,
      isExpanded,
      subItemSelections,
      note,
      questionsQueue,
      answers,
    ]
  )

  const advanceToNext = useCallback(async () => {
    if (currentQuestionIndex < questionsQueue.length - 1) {
      goToQuestion(currentQuestionIndex + 1)
      return
    }

    setAnswers((prev) => ({
      ...prev,
      [currentQuestionIndex]: {
        selectedOption,
        isExpanded,
        subItemSelections,
        note,
      },
    }))

    // A second account can finish separating while the user is working through the first
    // one's groups. Without this check those groups are never asked about, and the sync path
    // hands nothing to autocat during onboarding either.
    //
    // Compared by transaction, not by question id: that id is the merchant lookup key, so a
    // merchant both accounts share would look like one already answered and be dropped.
    const refreshed = await dispatch(fetchSeparationQuestions())
    const admitted = admittedQuestions({
      queue: questionsQueue,
      refreshed: refreshed?.questions ?? [],
      maxQuestions: refreshed?.maxQuestions ?? questionsQueue.length,
    })

    if (admitted.length === 0) {
      setIsFinishing(true)
      return
    }

    const grown = [...questionsQueue, ...admitted]
    setQuestionsQueue(grown)
    goToQuestion(questionsQueue.length, grown)
  }, [
    currentQuestionIndex,
    questionsQueue,
    selectedOption,
    isExpanded,
    subItemSelections,
    note,
    goToQuestion,
    dispatch,
  ])

  const handleBack = useCallback(() => {
    track('onboarding_books_question_back', {
      questionIndex: currentQuestionIndex,
    })

    if (currentQuestionIndex === 0) {
      postSeparationEvent('left')
      onBack?.()
      return
    }

    goToQuestion(currentQuestionIndex - 1)
  }, [track, currentQuestionIndex, onBack, goToQuestion])

  // The on-screen selection is dropped on purpose: only a Continue counts as an answer.
  // Skip is disabled while an answer saves: a failed save resolves rather than throws, so
  // leaving mid-save would release that question's rows as if it were never answered.
  const handleSkip = useCallback(() => {
    if (isSaving) return
    const openQuestions = totalSteps - currentQuestionIndex
    track('onboarding_books_question_skip', {
      screen: SeparationSkipScreen.Question,
      questionIndex: currentQuestionIndex,
      openQuestions,
    })
    onSkip(SeparationSkipScreen.Question, openQuestions)
  }, [isSaving, track, totalSteps, currentQuestionIndex, onSkip])

  const handleContinue = useCallback(() => {
    if (!selectedOption) {
      track('onboarding_books_question_continue_blocked', {
        questionIndex: currentQuestionIndex,
      })
      setShowSelectionError(true)
      return
    }

    const isMix = selectedOption === question.expandValue
    const unanswered = question.subItems.filter(
      (subItem) => !subItemSelections[subItem.id]
    )
    if (isMix && unanswered.length > 0) {
      track('onboarding_books_question_continue_blocked_sub_item', {
        questionIndex: currentQuestionIndex,
        unanswered: unanswered.length,
      })
      setShowSubItemError(true)
      return
    }

    if (hasUnresolvedUnsureNote(question, subItemSelections, note)) {
      track('onboarding_books_question_continue_blocked_unsure_note', {
        questionIndex: currentQuestionIndex,
      })
      setShowNoteError(true)
      return
    }

    track('onboarding_books_question_continue', {
      questionIndex: currentQuestionIndex,
      selectedOption,
      suggested: question.suggested || null,
      keptSuggestion: selectedOption === question.suggested,
    })

    // Saved per question rather than at the end, so a drop-off keeps what was already
    // answered, and awaited rather than fired and forgotten, so the flow can't carry the
    // user past an answer that never landed.
    const save = dispatch(
      submitSeparationAnswers(
        answersForQuestion(question, selectedOption, subItemSelections, note),
        askedHavenFor.has(currentQuestionIndex)
      )
    )
    pendingSavesRef.current.push(save)

    // `undefined` is what a handled failure resolves to, and it leaves the user on this
    // question with the error the fetch slice recorded.
    void save.then((result) => {
      if (!result) return
      showSaved()
      void advanceToNext()
    })
  }, [
    track,
    dispatch,
    currentQuestionIndex,
    selectedOption,
    subItemSelections,
    note,
    askedHavenFor,
    advanceToNext,
    showSaved,
    question,
  ])

  const handleSelectOption = useCallback(
    (option: string) => {
      track('onboarding_books_question_option_selected', { option })
      setSelectedOption(option)
      setShowSelectionError(false)

      if (option === question.expandValue) {
        setIsExpanded(true)
        return
      }
      if (option === selectedOption) return

      // Every row takes the group answer, so a note written for earlier choices is dropped.
      setSubItemSelections(
        Object.fromEntries(
          question.subItems.map((subItem) => [subItem.id, option])
        )
      )
      setNote('')
      setShowNoteError(false)
    },
    [track, question, selectedOption]
  )

  const handleExpandCard = useCallback(() => {
    if (question.alwaysExpanded) return
    if (isExpanded) {
      track('onboarding_books_question_card_collapsed')
      setIsExpanded(false)
      setExpandedRows({})
      return
    }
    track('onboarding_books_question_card_expanded')
    setIsExpanded(true)
  }, [track, isExpanded, question])

  const handleSelectSubItem = useCallback(
    (subItemId: string, value: string) => {
      track('onboarding_books_question_sub_item_selected', { subItemId, value })
      setShowSubItemError(false)
      setSubItemSelections((prev) => {
        const updated = { ...prev, [subItemId]: value }

        // Never auto-advance from sub-item interactions. Only a direct top-level
        // selection does that; mix flows always require a manual Continue.
        if (Object.keys(updated).length === question.subItems.length) {
          setSelectedOption(unanimousTopLevelOption(question, updated))
          setShowSelectionError(false)
        }

        return updated
      })
    },
    [track, question]
  )

  const handleToggleRow = useCallback((subItemId: string) => {
    setExpandedRows((prev) => ({ ...prev, [subItemId]: !prev[subItemId] }))
  }, [])

  const markAskedHaven = useCallback(() => {
    setAskedHavenFor((prev) => new Set(prev).add(currentQuestionIndex))
  }, [currentQuestionIndex])

  const handleAskHavenAboutGroup = useCallback(
    (scope: HavenEntryScope) => {
      markAskedHaven()
      track('onboarding_books_question_ask_haven', {
        merchant: question.headerName,
        scope,
      })
      launchHaven({
        context: { type: 'message', text: buildHavenGroupPrompt(question) },
        entryPoint: 'transactionSorting',
      })
    },
    [launchHaven, question, track, markAskedHaven]
  )

  const handleNoteChange = useCallback((updated: string) => {
    setNote(updated)
    if (updated.trim()) setShowNoteError(false)
  }, [])

  const handleGoToHome = useCallback(() => {
    track('onboarding_books_question_go_to_home')
    onContinue?.()
  }, [track, onContinue])

  const handleRetryRelease = useCallback(() => {
    void dispatch(completeSeparation()).then(refreshCounts)
  }, [dispatch, refreshCounts])

  const handleBackFromCompletion = useCallback(() => {
    track('onboarding_books_question_back_from_completion')
    setIsFinishing(false)
    setIsAllComplete(false)
  }, [track])

  if (isAllComplete) {
    return (
      <>
        <span ref={scrollRef} />
        <SortingSummary
          clarifiedCounts={clarifiedCountsFrom(answers)}
          releaseFailed={releaseFailed}
          isRetryingRelease={isRetryingRelease}
          completionFailed={completionFailed}
          isCompleting={isCompleting}
          checkIconRef={checkIconRef}
          onRetryRelease={handleRetryRelease}
          onGoToHome={handleGoToHome}
          onBack={handleBackFromCompletion}
        />
      </>
    )
  }

  return (
    <div className={ONBOARDING_SCREEN.page}>
      <span ref={scrollRef} />
      <div
        className={ONBOARDING_SCREEN.autosaveIndicator}
        style={{ right: AUTOSAVE_INSET + panelInset }}
        ref={autosaveIndicatorRef}
      >
        <Icon
          icon={faCheck}
          color="text-green-100"
          size="sm"
          backgroundClassName="size-auto"
        />
        <Text variant={TypographyStyles.LABEL_SM} color={TextColors.HIGHLIGHT}>
          Autosaved
        </Text>
      </div>
      <div className={ONBOARDING_SCREEN.content}>
        <div className={ONBOARDING_SCREEN.iconWrapper}>
          <Image
            src={BOOKS_QUESTION_ICON_URL}
            alt=""
            objectFit="contain"
            className={ONBOARDING_SCREEN.iconImage}
          />
        </div>

        <div
          className={mtc(
            ONBOARDING_SCREEN.header,
            ONBOARDING_SCREEN.narrowColumn
          )}
        >
          <Text variant={TypographyStyles.DISPLAY_SM} textAlign="center">
            We need your input
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
            textAlign="center"
            className="text-center"
          >
            {sortedCount > 0 && (
              <>
                We sorted <strong>{sortedCount.toLocaleString()}</strong>{' '}
                {transactionNoun(sortedCount)} automatically.{' '}
              </>
            )}
            We have {plural(totalSteps, 'one question', 'a few questions')} to
            help us learn about your business.
          </Text>
        </div>

        <div className="flex w-full max-w-180 items-center gap-6">
          <Progress size="default" value={progressValue} className="flex-1" />
          <Text
            variant={TypographyStyles.BODY_XS}
            color={TextColors.EMPHASIS}
            className="whitespace-nowrap"
          >
            {progressLabelValue} of {totalSteps}
          </Text>
        </div>

        <div className="flex w-full max-w-180 flex-col items-start gap-3">
          <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
            {question.questionText}
          </Text>

          <QuestionCard
            question={question}
            selectedOption={selectedOption}
            subItemSelections={subItemSelections}
            note={note}
            expandedRows={expandedRows}
            isExpanded={isExpanded}
            isExpandedView={isExpandedView}
            showSelectionError={showSelectionError}
            showSubItemError={showSubItemError}
            showNoteError={showNoteError}
            saveFailed={saveFailed}
            scrollportRef={scrollportRef}
            scrollportPaddingTop={scrollportPaddingTop}
            onExpandCard={handleExpandCard}
            onSelectOption={handleSelectOption}
            onSelectSubItem={handleSelectSubItem}
            onNoteChange={handleNoteChange}
            onToggleRow={handleToggleRow}
            onAskHavenAboutGroup={handleAskHavenAboutGroup}
          />
        </div>
      </div>

      <OnboardingStickyNav
        leftOffset={leftOffset}
        backIcon={null}
        continueIcon={null}
        className={ONBOARDING_SCREEN.footer}
        onBack={handleBack}
        backDisabled={isFinishing || isSaving}
        onContinue={handleContinue}
        continueDisabled={isFinishing || isSaving}
        continueLabel={isSaving ? 'Saving…' : 'Continue'}
        onSkip={isFinishing ? undefined : handleSkip}
        skipDisabled={isSaving}
        skipLabel="Skip"
      />
    </div>
  )
}

/** The flow's own chrome while the groups are still being built, so the wait reads as a step
 * of the flow rather than as a page that failed to render. */
const SortingInProgress = ({ onSkip }: { onSkip: () => void }) => (
  <div className={ONBOARDING_SCREEN.page}>
    <div
      className={mtc(
        ONBOARDING_SCREEN.content,
        'flex-1 justify-center text-center'
      )}
    >
      <div className={ONBOARDING_SCREEN.iconWrapper}>
        <Image
          src={BOOKS_QUESTION_ICON_URL}
          alt=""
          objectFit="contain"
          className={ONBOARDING_SCREEN.iconImage}
        />
      </div>
      <div
        className={mtc(
          ONBOARDING_SCREEN.header,
          ONBOARDING_SCREEN.narrowColumn
        )}
      >
        <Text variant={TypographyStyles.DISPLAY_SM} textAlign="center">
          Sorting your transactions
        </Text>
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          textAlign="center"
        >
          This only takes a moment. We are grouping what is left so we can ask
          you as few questions as possible.
        </Text>
      </div>
      <Button variant="secondary" onClick={onSkip}>
        Skip to dashboard
      </Button>
    </div>
  </div>
)

/**
 * Fetches the groups separation left unresolved, then hands them to the view. Split so the
 * view can keep dereferencing `questionsQueue[index]` unconditionally: it never mounts
 * without at least one question.
 */
export const BooksQuestion = ({
  onBack,
  onContinue,
  onSkip,
}: BooksQuestionProps) => {
  const { questions, sortedCount, status, loadFailed, refetch } =
    useSeparationQuestions()
  const track = useAnalyticsTrack()
  const skipFrom = (screen: SeparationSkipScreen) => () => {
    const openQuestions = questions?.length ?? 0
    track('onboarding_books_question_skip', { screen, openQuestions })
    onSkip(screen, openQuestions)
  }
  const isCompleting = useReselector(getIsFetching, COMPLETE_ONBOARDING_KEY)
  const completionFailed = Boolean(
    useReselector(getFetchError, COMPLETE_ONBOARDING_KEY)
  )

  // Holds the last non-empty set: the summary's own refetch empties the queue, and the
  // empty state belongs only to arriving with no work at all.
  const answeredQuestionsRef = useRef<typeof questions>(null)
  if (questions !== null && questions.length > 0) {
    answeredQuestionsRef.current = questions
  }
  const questionsToRender =
    questions !== null && questions.length > 0
      ? questions
      : answeredQuestionsRef.current

  if (loadFailed) {
    return (
      <SeparationLoadError
        onRetry={() => void refetch()}
        onBack={onBack}
        onSkip={skipFrom(SeparationSkipScreen.LoadError)}
      />
    )
  }

  if (questions === null || status === 'pending') {
    return (
      <SortingInProgress onSkip={skipFrom(SeparationSkipScreen.InProgress)} />
    )
  }

  if (questionsToRender === null) {
    return (
      <NothingToSort
        onContinue={onContinue}
        sortedCount={sortedCount}
        completionFailed={completionFailed}
        isCompleting={isCompleting}
      />
    )
  }

  return (
    <BooksQuestionView
      onBack={onBack}
      onContinue={onContinue}
      onSkip={onSkip}
      questions={questionsToRender}
      refreshCounts={refetch}
    />
  )
}

export default BooksQuestion
